import { matchPath } from "react-router";
import { SETTINGS_ROUTES } from "../../../../consts/routes";
import { BLOCK_FIELDS, TITLE_ELEMENT_ID } from "./config";
import { removeNullishFromShape } from "../../../../utilities/general";
import { SURVEY_TYPE } from "../../../../api/surveys/config";

export const getPathParams = () => {
  const match = matchPath(window.location.pathname, {
    path: SETTINGS_ROUTES.editSurveyTemplate(":id"),
    exact: true,
    strict: false,
  });

  const surveyId = Number(match?.params?.id || "0");

  return {
    surveyId,
    isEditMode: Boolean(surveyId),
  };
};

export const blockAdapter = {
  send: (block, index, isEditMode) => {
    const id = block[BLOCK_FIELDS.id];
    return removeNullishFromShape({
      id: isEditMode && typeof id === "number" ? id : null,
      boxtype: block[BLOCK_FIELDS.type],
      description: block[BLOCK_FIELDS.isDescriptionRequired] || false,
      descriptionText: block[BLOCK_FIELDS.description] || "",
      displayOrder: index + 1,
      name: block[BLOCK_FIELDS.question] || "",
      options: block[BLOCK_FIELDS.choices] || [],
      required: block[BLOCK_FIELDS.isRequired] || false,
      why_choose: block[BLOCK_FIELDS.commentsOnScale] || false,
    });
  },
  receive: (block) => ({
    [BLOCK_FIELDS.id]: block.id,
    [BLOCK_FIELDS.type]: block.boxtype,
    [BLOCK_FIELDS.question]: block.name,
    [BLOCK_FIELDS.choices]: block.options || [],
    [BLOCK_FIELDS.isRequired]: block.required || false,
    [BLOCK_FIELDS.isDescriptionRequired]: block.description || false,
    [BLOCK_FIELDS.description]: block.descriptionText || "",
    [BLOCK_FIELDS.commentsOnScale]: block.why_choose || false,
  }),
};

/**
 * @returns {Array<{
 *  message: string;
 *  elementId: string | null;
 * }>}
 */
export var preSubmitValidation = ({ title, questions }) => {
  var getTitleError = (title) => (!title?.trim() ? "titleRequired" : null);

  var getQuestionsLengthError = (questions) =>
    questions.length === 0 ? "questionRequired" : null;

  var getDescriptionErrors = (questions) =>
    questions
      .map((block) => {
        if (block.isDescriptionRequired && !block.description?.trim()) {
          return {
            message: "descriptionRequired",
            elementId: block.id,
          };
        }
        return null;
      })
      .filter(Boolean);

  var getQuestionsChoicesErrors = (questions) =>
    questions
      .filter((block) =>
        [SURVEY_TYPE.singleChoice, SURVEY_TYPE.multipleChoice].includes(
          block.type,
        ),
      )
      .map(({ id, choices }) => {
        if (choices.map((x) => x?.trim()).filter(Boolean).length === 0) {
          return {
            message: "questionChoicesRequired",
            elementId: id,
          };
        }
        if (choices.length !== new Set(choices).size) {
          return {
            message: "duplicateChoices",
            elementId: id,
          };
        }
        return null;
      })
      .filter(Boolean);

  var questionNamesValid = (questions) => {
    var names = [];
    return questions
      .map((block) => {
        if (names.includes(block.question)) {
          return {
            message: "duplicateQuestionNames",
            elementId: block.id,
          };
        }

        if (!block.question?.trim()) {
          return {
            message: "questionNameRequired",
            elementId: block.id,
          };
        }

        names.push(block.question);

        return null;
      })
      .filter(Boolean);
  };

  var errors = [];

  var titleError = getTitleError(title);
  var questionsLengthError = getQuestionsLengthError(questions);
  var questionsChoicesErrors = getQuestionsChoicesErrors(questions);
  var questionNamesErrors = questionNamesValid(questions);
  var descriptionErrors = getDescriptionErrors(questions);

  if (titleError) {
    errors.push({
      message: titleError,
      elementId: TITLE_ELEMENT_ID,
    });
  }

  if (questionsLengthError) {
    errors.push({
      message: questionsLengthError,
      elementId: null,
    });
  }

  if (questionsChoicesErrors.length > 0) {
    errors = [...errors, ...questionsChoicesErrors];
  }

  if (questionNamesErrors.length > 0) {
    errors = [...errors, ...questionNamesErrors];
  }

  if (descriptionErrors.length > 0) {
    errors = [...errors, ...descriptionErrors];
  }

  return errors;
};

export var groupErrors = (errors) =>
  errors.reduce((carry, { elementId, message }) => {
    if (carry[elementId]) {
      return {
        ...carry,
        [elementId]: [...carry[elementId], message],
      };
    } else {
      return {
        ...carry,
        [elementId]: [message],
      };
    }
  }, {});
